import React, { useEffect, useMemo } from "react";

/**
 * Single-file, preview-safe article page.
 * - No external imports (prevents "File not found" errors in the editor).
 * - Paste your legacy HTML into `legacyHtml` and it will render inside the site frame.
 * - Strips <head>/<style>/<script>/<link> so it doesn't break your app styling.
 * - Optional image rewrite: /images/... -> /images/articles/...
 */

const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  lilac: "#a78bfa", // sponsor-only
};

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return <div className={`border border-black/15 bg-white ${className}`}>{children}</div>;
}

function SafeIcon() {
  // Preview-safe icon (no asset import). Replace with your real logo icon in your app.
  return (
    <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="6" y="6" width="52" height="52" fill="#0b0b0f" />
      <rect x="12" y="12" width="40" height="40" fill="#ffffff" />
      <path d="M18 42V22h10c7 0 12 4 12 10s-5 10-12 10H18zm6-5h4c4 0 7-2 7-5s-3-5-7-5h-4v10z" fill="#0b0b0f" />
    </svg>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music × Culture
              </a>
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art × Fashion
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </a>
              <a
                href="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </a>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-end gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">Culture intelligence → creative execution</div>
            </div>

            <a href="/" className="mx-auto flex items-center gap-3 text-center" aria-label="Go to Home">
              <SafeIcon />
              <div>
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl">Africa Loud</div>
                <div className="-mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </a>

            <div className="hidden justify-self-end sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Weekly drops</div>
              <div className="mt-1 text-xs text-neutral-600">Signals · Cities · Strategy</div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-black text-white"
              >
                Culture × Music
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Art × Fashion
              </a>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Subscribe</div>
              <div className="h-3 w-px bg-black/15" />
              <div className="text-[11px] text-neutral-600">One email · one week</div>
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-800 px-5 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-white">Africa Loud</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 text-xs text-white/70">© 2026 Africa Loud</div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Partner</div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">ON THIRD - Premium Luxury</div>
            <div className="mt-1 text-sm text-neutral-700">Shop the scent with a 10% off offer via the partner link.</div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
            style={{ borderColor: BRAND.lilac }}
          >
            Shop the scent · 10% off
            <span className="ml-2">→</span>
          </a>
        </div>
      </Box>
    </div>
  );
}

function stripLegacyHtml(html) {
  // Remove wrappers/head assets/scripts so legacy pages don’t hijack styling.
  let out = html || "";

  // Remove doctype/html/head/body tags entirely
  out = out
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "");

  // Remove scripts/styles/links (common in pasted legacy pages)
  out = out
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "");

  // Kill on* handlers (onclick, onload, etc.) to avoid runtime errors
  out = out.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son\w+\s*=\s*'[^']*'/gi, "");

  return out.trim();
}

function rewriteLegacyImages(html, prefix = "/images/articles/") {
  // /images/foo.png -> /images/articles/foo.png
  return (html || "").replace(/(src=\s*["'])\/?images\//gi, `$1${prefix}`);
}

function normalizeAnchors(html) {
  // If you have legacy links like /echo/articles/x.html, you can remap them here.
  // Current behavior: keep links as-is.
  return html || "";
}

function ArticlePageTemplate({
  seoTitle,
  seoDescription,
  canonicalPath,
  heroTitle,
  heroByline,
  heroImage,
  legacyHtml,
  stripStyles = true,
  rewriteImages = true,
}) {
  const preparedHtml = useMemo(() => {
    let out = legacyHtml || "";
    if (stripStyles) out = stripLegacyHtml(out);
    if (rewriteImages) out = rewriteLegacyImages(out);
    out = normalizeAnchors(out);
    return out;
  }, [legacyHtml, stripStyles, rewriteImages]);

  useEffect(() => {
    // Minimal SEO in preview: update title/description.
    if (typeof document === "undefined") return;
    if (seoTitle) document.title = seoTitle;

    if (seoDescription) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", seoDescription);
    }

    // Canonical (safe in most apps; in this preview it’s fine too)
    if (canonicalPath) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonicalPath);
    }
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-8">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">Culture × Music</div>
          </div>

          {/* Hero */}
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <Box className="p-6 sm:p-8">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Feature</div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl">{heroTitle}</h1>
              {heroByline ? <div className="mt-3 text-sm text-neutral-600">{heroByline}</div> : null}
              <div className="mt-6">
                <Rule />
                <div className="mt-3 h-1 w-full" style={{ backgroundColor: BRAND.burgundy }} />
              </div>
            </Box>

            <Box className="relative overflow-hidden">
              <div
                className="h-full min-h-[280px] w-full"
                style={{
                  backgroundImage: `url('${heroImage}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0">
                <div className="bg-white/92 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Read</div>
                      <div className="mt-1 text-base font-semibold text-neutral-950">Africa Loud</div>
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BRAND.burgundy }}>
                      Director's pick
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Sponsor stays on all pages */}
          <SponsorStrip />

          {/* Article body */}
          <div className="mt-8 pb-12">
            <Box className="p-6 sm:p-10">
              <article
                className="max-w-none text-neutral-900 leading-relaxed [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:tracking-tight [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:tracking-tight [&_ul]:my-5 [&_ul]:pl-6 [&_li]:mb-2 [&_a]:underline [&_a]:decoration-black/30 hover:[&_a]:decoration-black"
                dangerouslySetInnerHTML={{ __html: preparedHtml }}
              />
            </Box>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// --- “Tests” (runs in dev/preview) ---
function runTemplateSanityTests() {
  // eslint-disable-next-line no-console
  console.assert(stripLegacyHtml("<script>alert(1)</script><p>ok</p>").includes("<p>ok</p>"), "stripLegacyHtml should remove script");
  // eslint-disable-next-line no-console
  console.assert(!stripLegacyHtml("<style>h1{}</style><p>ok</p>").includes("<style"), "stripLegacyHtml should remove style");
  // eslint-disable-next-line no-console
  console.assert(
    rewriteLegacyImages('<img src="/images/a.png" />').includes('/images/articles/a.png'),
    "rewriteLegacyImages should rewrite /images/"
  );
}

export default function BeyonceArticle() {
  useEffect(() => {
    if (typeof window !== "undefined") runTemplateSanityTests();
  }, []);

  return (
    <ArticlePageTemplate
      seoTitle="YOUR HISTORY IS IN YOUR LANGUAGE"
      seoDescription=""
      canonicalPath="/culture-music/town_talk"
      heroTitle="YOUR HISTORY IS IN YOUR LANGUAGE"
      heroByline="By Olwethu M. Bebeza"
      
      heroImage="/images/articles/#"
      stripStyles={true}
      rewriteImages={true}
      legacyHtml={`
   <div class="content">
<article>
  <section>
    <p><strong>Let’s be clear, cultural expression is power.</strong> In a world hyper-connected through TikTok trends, global tours, and Spotify algorithms, the sounds that travel farthest aren't always the ones that scream “mainstream.” Sometimes, they’re the ones that stay true to where they came from.</p>

    <p>This is the story of three genre giants, <strong>K-Pop, Afrobeats, and Amapiano</strong>, and how each is reshaping global music without losing their roots. If you're Gen Z and proudly African, this isn’t just a wave, it’s a blueprint for power without compromise.</p>
  </section>

  <section>
    <h2 >Cultural Export vs Cultural Compromise</h2>
    <p>When a music style becomes global, artists and labels face a fork in the road: adapt the sound to fit global norms (often, Western ones), or double down on what makes the sound unique. <strong>K-Pop</strong>, and more recently, <strong>Afrobeats and Amapiano</strong>, show us that keeping it local can also make you global.</p>

    <p>Consider BTS, the global K-Pop juggernaut. Despite selling out stadiums around the world, BTS rarely performs in English. They rap, sing, and speak in Korean. Their success isn’t in spite of that choice, it’s because of it. Their language, fashion, gestures, and cultural symbolism remain intact. The world came to them.</p>

    <p>Now, look at Africa. As <strong>Afrobeats</strong> and <strong>Amapiano</strong> continue to go global, there’s a lesson here. We don’t need to “sound more Western” or “speak less Yoruba, Zulu, or Pidgin.” What’s local is already magnetic. What’s authentic already sells.</p>
  </section>

  <section>
  <h2>Voices That Didn’t Compromise</h2>
  <p>These artists didn’t just win, they won by staying rooted. Their careers remind us that culture isn’t a barrier. It’s the brand.</p>

  <div >
    <h3 >RM (BTS Leader)</h3>
    <p><em>"We’re proud of our language. We love our culture. We believe we can tell our stories in Korean and still reach the world."</em></p>
    <p>RM's vision helped BTS maintain their identity while topping the Billboard charts. Their global rise proved that <strong>you don’t have to translate to be understood, you just have to be authentic</strong>.</p>
  </div>

  <div >
    <h3 > Rema (Afrobeats Star)</h3>
    <p><em>"I speak my truth in pidgin because that's how I feel it. I don’t need to be anything else to go global."</em></p>
    <p>Rema’s sound isn’t polished for Western ears, it’s a direct transmission from Benin City to the world. From <strong>"Calm Down"</strong> to his Dior runway moment, he’s made it clear: <strong>Naija to the world doesn’t require a passport. Just purpose.</strong></p>
  </div>

  <div >
    <h3 >Tyla (South African Breakout Star)</h3>
    <p><em>"I don’t think I need to change Amapiano to make it global. The sound is enough. The culture is enough."</em></p>
    <p>Tyla didn’t tone down her roots on <strong>"Water"</strong>, she danced in dust, echoed township energy, and let the beat speak. That authenticity landed her on Jimmy Fallon and at the Met Gala. Amapiano wasn’t diluted — it was amplified.</p>
  </div>

  <p>These voices, RM, Rema, Tyla, prove one thing: when you lead with who you are, the world doesn’t just listen. <strong>It leans in.</strong></p>
</section>


  <section>
    <h2 >The People Behind the Sounds</h2>
    <p>Every sound has a people. And every people has a culture, a spirit, a code.</p>
    
    <p>Behind <strong>BTS</strong> is a powerful ecosystem: South Korean producers, choreographers, stylists, and an army of fans (called “ARMY”) who obsessively support their idols. But behind all that is Korean culture: Confucian values, collectivism, fashion, food, and language that never bends for global approval.</p>
    
    <p><strong>Afrobeats</strong> isn’t just a sound, it’s Lagos traffic, Ghanaian street slang, Naija resilience. Artists like <strong>Burna Boy</strong> and <strong>Rema</strong> have become symbols of an Africa that refuses to shrink. The beats are global, but the flow is proudly African.</p>
    
    <p><strong>Amapiano</strong> is township culture in audio form. It’s born in Pretoria, shaped in Soweto, and danced in Braamfontein. The log drums, the slowed tempo, the breathy vocals, they all come from <strong>real people with real struggles and real vibes</strong>.</p>
  </section>

  <section>
    <h2 >Subcultures Make It Travel</h2>
    <p>Want to know why these genres fly?</p>

    <ul>
      <li><strong>K-Pop</strong> = Idol Culture + Fashion Aesthetic + Fan Power</li>
      <li><strong>Afrobeats</strong> = Flashy Lifestyle + Skits + Street Slang</li>
      <li><strong>Amapiano</strong> = Dance Challenges + Township Cool + DIY Spirit</li>
    </ul>

    <p>Genres don’t blow up on music alone, they blow up through culture. When <strong>Uncle Waffles</strong> drops a set in a crop top and bucket hat, it’s not just music, it’s fashion. When <strong>DBN Gogo</strong> headlines Boiler Room with an all-women lineup, it's not just a party, it's a statement.</p>

    <p>Every time BTS drops a music video full and song full of Hangul lyrics, they're flexing Korea’s cultural confidence.</p>

    <p>In the same way, Africa must continue to build subcultures around our genres, not dilute them. From #PianoToTheWorld to #NaijaNoDeyCarryLast, the slogans already exist. Now it's about <strong>building intentional cultural ecosystems around them</strong>.</p>
  </section>

  <section>
    <h2 >The Power of Staying True</h2>
    <p>When you export culture without compromise, you send a signal: our way of life is valid, our language is enough, our beat is global. And people respond. Because in a saturated world of sameness, difference is the ultimate flex.</p>

    <p>Take Rema’s <em>Calm Down</em>. It became a global hit not because it was American-friendly, but because it was Naija. Or consider Tyla’s <em>Water</em>. While people argue whether it's Afrobeats or not, Tyla herself has stayed rooted in Amapiano and South African pop identity. The visuals? Afro-glam. The moves? Skomota-inspired. The vibe? Unapologetically South African.</p>

    <p>That’s the lesson. Don’t dilute. Define.</p>
  </section>

  <section>
    <h2 >In a Globalized Economy, Culture is Currency</h2>
    <p>We are living in an era where culture <strong>is</strong> the product. When TikTok sounds can launch careers, when Netflix is commissioning stories from Lagos to Seoul, and when Spotify curates playlists by “vibe” not language, we need to understand: <strong>our cultural exports are economic exports.</strong></p>

    <p>The rise of <strong>K-content</strong> (K-dramas, K-beauty, K-pop) added billions to South Korea’s GDP. Why? Because they believed their culture could sell. And it did.</p>

    <p>Now Africa must ask itself: will we water down our sounds to fit someone else’s format, or will we build an industry around our truth, our dances, our slang, our aesthetic?</p>
  </section>

  <section>
    <h2 >What Makes the Genre Work?</h2>
    <p><strong>K-Pop:</strong> It works because it creates <em>idols</em>, super-trained, fan-connected, fashion-forward performers. It’s aspirational but deeply cultural. The symbolism is high: cherry blossoms for youth, hanbok patterns for heritage, cosmic visuals for destiny. Everything means something.</p>

    <p><strong>Afrobeats:</strong> It thrives because it is rhythm + rebellion. From the money gun visuals to private jet lyrics, it’s about freedom, flex, and being unapologetic in a world that once muted African voices. Afrobeats turns luxury into a symbol of survival.</p>

    <p><strong>Amapiano:</strong> It pulses because it’s a lived experience. You don’t “listen” to Amapiano, you move to it. You post a dance. You grab a soundbite. You join a challenge. The genre created its own aesthetic: soft pastel clothes, groovy moves, and spiritual minimalism. It’s not just sound, it’s street anthropology.</p>
  </section>

  <section>
    <h2 >So, What’s Next?</h2>
    <p><strong>Here’s the move:</strong></p>
    <ul>
      <li>Create intentional subcultures around African genres, fashion, slang, memes, art.</li>
      <li>Support the people behind the scenes: choreographers, stylists, directors, dancers.</li>
      <li>Celebrate the local dialects, phrases, and references in our lyrics, don’t water them down.</li>
      <li>Push local platforms and publications (yes, like <strong>Africa Loud</strong>) to be the voice of the culture, not just observers of it.</li>
      <li>Educate the audience. If BTS fans can learn Korean phrases and celebrate hanbok, then global fans can learn what “shaya” means in Zulu or “Odogwu” in Igbo.</li>
    </ul>
    <p>And most importantly: don’t seek validation, set standards.</p>
  </section>

  <section>
    <h2 >Final Word: Africa, Own Your Echo</h2>
    <p>The world doesn’t need another copy. It needs the original. From K-Pop to Amapiano, the winning strategy isn’t mimicry. It’s mastery of your own rhythm.</p>

    <p><strong>To the dancers in Jozi, the stylists in Accra, the videographers in Kinshasa, and the beatmakers in Nairobi:</strong> you are not behind. You are the blueprint.</p>

    <p>Export your story. Keep your accent. Let your genre wear the language of your land. Because when we lead with truth, the whole world listens, and dances.</p>
    
    
  </section>
</article>
</div>


 `}
    />
  );
}
